import React from "react";
import Loader from "../Loader";
import EmptyState from "./EmptyState";

// Tableau de données piloté par une spec de colonnes :
// { key, header, render(row, index), headerSrOnly, className, cellClassName, width }
// États : squelette au premier chargement, voile de rechargement quand des
// lignes existent déjà (pas de saut de hauteur), erreur, vide, en attente.
// `rowClassName(row, index)` pose une classe par ligne : les liserés d'état
// `row--ok` / `row--warn` / `row--ko` sont définis dans data-list.scss.
const DataTable = ({
  columns = [],
  rows = [],
  rowKey = "id",
  caption = null,
  isLoading = false,
  hasSearched = true,
  error = null,
  emptyState = {},
  idleState = null,
  skeletonRows = 5,
  className = "",
  rowClassName = null,
}) => {
  const getRowClassName = (row, index) => {
    if (!rowClassName) return undefined;
    const value = typeof rowClassName === "function" ? rowClassName(row, index) : rowClassName;
    return value || undefined;
  };
  const getKey = (row, index) => {
    if (typeof rowKey === "function") return rowKey(row, index);
    const key = row[rowKey];
    return key === undefined || key === null ? index : key;
  };
  const showSkeleton = isLoading && rows.length === 0;
  const showOverlay = isLoading && rows.length > 0;
  const showError = !isLoading && !!error;
  const showEmpty = !isLoading && !error && rows.length === 0 && hasSearched;
  const showIdle = !isLoading && !error && rows.length === 0 && !hasSearched;

  return (
    <div className={`data-table ${className}`.trim()}>
      <div className="data-table__scroll" aria-busy={isLoading || undefined}>
        <table className="table data-table__table">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={c.className}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.headerSrOnly ? <span className="sr-only">{c.header}</span> : c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={getKey(row, i)} className={getRowClassName(row, i)}>
                {columns.map((c) => (
                  <td key={c.key} className={c.cellClassName || c.className}>
                    {c.render ? c.render(row, i) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {showSkeleton &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="data-table__skeleton" aria-hidden="true">
                  {columns.map((c, j) => (
                    <td key={c.key}>
                      <span
                        className="skeleton-bar"
                        style={{ width: `${55 + ((i + j) % 3) * 15}%` }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            {showError && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="empty-state empty-state--error" role="alert">
                    <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                    <span className="empty-state__title">{error}</span>
                  </div>
                </td>
              </tr>
            )}
            {showEmpty && (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState
                    icon={emptyState.icon}
                    title={emptyState.title || "Aucun résultat"}
                    hint={
                      emptyState.hint === undefined
                        ? "Modifiez votre recherche ou vos filtres."
                        : emptyState.hint
                    }
                    action={emptyState.action}
                  />
                </td>
              </tr>
            )}
            {showIdle && (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState
                    icon={(idleState && idleState.icon) || "fa-search"}
                    title={(idleState && idleState.title) || "Lancez une recherche"}
                    hint={idleState && idleState.hint}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {showOverlay && (
          <div className="data-table__loading" role="status">
            <Loader />
            <span className="sr-only">Chargement…</span>
          </div>
        )}
        {showSkeleton && (
          <span className="sr-only" role="status">
            Chargement…
          </span>
        )}
      </div>
    </div>
  );
};

export default DataTable;
